import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  OpenImageViewerShell,
  type OpenImageViewerConfig,
  type OpenImageViewerLoadedImage,
} from '@/features/open-image-viewer'
import {
  SvgConversionError,
  convertSvg,
  downloadBlob,
  outputFilename,
  prepareSvgFile,
  type LoadedSvg,
  type SvgOutputFormat,
} from '@/features/svg-converter'
import { useT } from '@/shared/i18n'

const UTILITY_KEY = 'svg-viewer'
const MAX_FILE_SIZE = 10 * 1024 * 1024

interface SvgExportActionsProps {
  svg: LoadedSvg
}

function SvgExportActions({ svg }: SvgExportActionsProps) {
  const t = useT()
  const copy = t.svgViewer
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const exportGenerationRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      exportGenerationRef.current += 1
    }
  }, [])

  const exportSvg = useCallback(
    async (format: SvgOutputFormat) => {
      const exportGeneration = ++exportGenerationRef.current
      const isActive = () =>
        mountedRef.current && exportGeneration === exportGenerationRef.current
      const startTime = performance.now()
      toolInvoke(UTILITY_KEY, { action: 'export', format })
      setError(null)

      if (!svg.dimensionsDetected) {
        setError(copy.exportMissingDimensions)
        toolError(UTILITY_KEY, 'missing_dimensions', { action: 'export', format })
        return
      }

      setIsExporting(true)
      let resultUrl: string | null = null
      try {
        const result = await convertSvg(
          svg,
          format,
          svg.width,
          svg.height,
          '#ffffff',
          copy.processingErrors,
        )
        resultUrl = result.url
        if (!isActive()) return
        downloadBlob(result.blob, outputFilename(svg.file.name, format))
        toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
          action: 'export',
          format,
          width: result.width,
          height: result.height,
        })
      } catch (caught) {
        if (!isActive()) return
        const code =
          caught instanceof SvgConversionError ? caught.code : 'conversion_failed'
        setError(caught instanceof Error ? caught.message : copy.exportFailed)
        toolError(UTILITY_KEY, code, { action: 'export', format })
      } finally {
        if (resultUrl) URL.revokeObjectURL(resultUrl)
        if (isActive()) setIsExporting(false)
      }
    },
    [copy.exportFailed, copy.exportMissingDimensions, copy.processingErrors, svg],
  )

  return (
    <div className="space-y-3">
      <p className="text-sm leading-5 text-muted-foreground">
        {copy.exportDescription}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          disabled={isExporting}
          onClick={() => void exportSvg('png')}
        >
          {isExporting ? copy.exporting : copy.exportPng}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={isExporting}
          onClick={() => void exportSvg('jpg')}
        >
          {isExporting ? copy.exporting : copy.exportJpg}
        </Button>
      </div>
      {error && (
        <p className="text-sm leading-5 text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function getLoadedSvg(image: OpenImageViewerLoadedImage): LoadedSvg | null {
  const data = image.data
  if (
    !data ||
    typeof data !== 'object' ||
    !('url' in data) ||
    !('file' in data)
  ) {
    return null
  }
  return data as LoadedSvg
}

export function SvgViewerPage() {
  const t = useT()
  const copy = t.svgViewer

  const config: OpenImageViewerConfig = {
    legacyAnalyticsKey: UTILITY_KEY,
    accept: '.svg,image/svg+xml',
    acceptedMimeTypes: ['image/svg+xml'],
    acceptedExtensions: ['.svg'],
    maxFileSizeBytes: MAX_FILE_SIZE,
    formatLabel: 'SVG',
    previewBackground: 'checkerboard',
    loadImage: async (file) => {
      const svg = await prepareSvgFile(file, copy.processingErrors)
      return {
        previewUrl: svg.url,
        width: svg.width,
        height: svg.height,
        dimensionsLabel: svg.dimensionsDetected
          ? `${svg.width} x ${svg.height}`
          : copy.dimensionsMissing,
        mimeLabel: file.type || 'image/svg+xml',
        formatLabel: 'SVG',
        data: svg,
        revoke: () => URL.revokeObjectURL(svg.url),
      }
    },
    mapLoadError: (error) =>
      error instanceof SvgConversionError
        ? { message: error.message, code: error.code }
        : null,
    renderActions: ({ image }) => {
      const svg = getLoadedSvg(image)
      return svg ? <SvgExportActions svg={svg} /> : null
    },
    convertHref: '/svg-v-png-jpg',
    relatedTools: copy.relatedTools,
  }

  return <OpenImageViewerShell copy={copy} config={config} />
}
